import { Fragment, useState, type ReactNode } from 'react';
import type { Candidate } from '@ni/shared';
import { defaultCorrections } from './candidate-card';
import { groupLinked } from './linked-groups';
import { type Corrections, uniqueCorrections } from './same-person';
import { WorkingButton } from '../ui/working-button';
import {
  LIKELY_SAME_CONFLICT,
  LIKELY_SAME_HEAD,
  LIKELY_SAME_SHARED,
  SAME_PERSON_WORKING,
  likelySameConfirmLabel,
  likelySameFoot,
} from '../../lib/copy';

/**
 * The cards of a list the office can bind from — the pick list and the held
 * list — with the ones the search thinks are one person drawn together
 * (2026-10-14). A group says what its cards share and what disagrees, and its
 * one button sends them as one person, each card's near-match answers included.
 * Every card keeps its own button, and picking one leaves the rest of its group
 * open rather than recording them as someone else (the server's rule).
 *
 * In same-person mode the group's button gives way to the cards' checkboxes.
 */
export function CandidateList({
  candidates,
  card,
  onConfirmGroup,
  workingGroup,
  disabled,
  selecting,
}: {
  candidates: readonly Candidate[];
  /** One card, wired to report its near-match answers to its group. */
  card: (
    candidate: Candidate,
    onCorrectionsChange: (corrections: Corrections) => void,
  ) => ReactNode;
  onConfirmGroup: (refs: string[], corrections: Corrections) => void;
  /** The first ref of the group whose answer is in flight, if any. */
  workingGroup: string | null;
  disabled: boolean;
  /** Same-person mode is open. */
  selecting: boolean;
}) {
  const [answers, setAnswers] = useState<Record<string, Corrections>>({});
  const report = (ref: string) => (corrections: Corrections) =>
    setAnswers((current) => ({ ...current, [ref]: corrections }));

  return (
    <div className="candidates">
      {groupLinked(candidates).map((item) => {
        if (item.kind === 'card')
          return (
            <Fragment key={item.candidate.candidate_ref}>
              {card(item.candidate, report(item.candidate.candidate_ref))}
            </Fragment>
          );
        const refs = item.members.map((m) => m.candidate_ref);
        const key = refs[0] as string;
        return (
          <div className="linked-group" key={key}>
            <div className="linked-head">
              <div className="linked-why">
                <div className="linked-title">{LIKELY_SAME_HEAD}</div>
                {item.shared.map((line) => (
                  <div className="linked-shared" key={`s-${line}`}>
                    <span className="linked-label">{LIKELY_SAME_SHARED}</span> {line}
                  </div>
                ))}
                {item.conflicts.map((line) => (
                  <div className="linked-conflict" key={`c-${line}`}>
                    <span className="linked-label">{LIKELY_SAME_CONFLICT}</span> {line}
                  </div>
                ))}
              </div>
              {selecting ? null : (
                <WorkingButton
                  label={likelySameConfirmLabel(refs.length)}
                  workingLabel={SAME_PERSON_WORKING}
                  working={workingGroup === key}
                  disabled={disabled && workingGroup !== key}
                  onClick={() =>
                    onConfirmGroup(
                      refs,
                      uniqueCorrections(
                        item.members.flatMap(
                          (m) => answers[m.candidate_ref] ?? defaultCorrections(m),
                        ),
                      ),
                    )
                  }
                />
              )}
            </div>
            {item.members.map((member) => (
              <Fragment key={member.candidate_ref}>
                {card(member, report(member.candidate_ref))}
              </Fragment>
            ))}
            <div className="linked-foot">{likelySameFoot(refs.length)}</div>
          </div>
        );
      })}
    </div>
  );
}
